import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Tab, Tabs, Grid, Button, Alert,
  LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, TextField, Select, MenuItem, FormControl, InputLabel, Divider
} from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { format, parseISO } from 'date-fns';
import { reportsApi } from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

function TabPanel({ value, index, children }: any) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

const COLORS = ['#1565c0', '#0288d1', '#00897b', '#558b2f', '#f57f17', '#e53935'];

export default function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'renewals' ? 1 : 0;

  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contractReport, setContractReport] = useState<any>(null);
  const [obligationReport, setObligationReport] = useState<any>(null);
  const [renewalReport, setRenewalReport] = useState<any>(null);
  const [renewalDays, setRenewalDays] = useState(90);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const [contractRes, oblRes, renewalRes] = await Promise.all([
        reportsApi.contracts(),
        reportsApi.obligations(),
        reportsApi.renewals({ days: renewalDays })
      ]);
      setContractReport(contractRes.data);
      setObligationReport(oblRes.data);
      setRenewalReport(renewalRes.data);
    } catch (err: any) {
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  const handleExportContracts = async () => {
    try {
      const res = await reportsApi.exportContracts();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contracts-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch {}
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v);

  const formatDate = (d: string) => {
    try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Reports & Analytics</Typography>
        <Box display="flex" gap={1}>
          <Button startIcon={<Refresh />} onClick={loadReports} disabled={loading} variant="outlined" size="small">Refresh</Button>
          <Button startIcon={<Download />} onClick={handleExportContracts} variant="outlined" size="small">Export CSV</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Contract Overview" />
          <Tab label="Renewals & Expirations" />
          <Tab label="Obligations & SLAs" />
        </Tabs>

        <CardContent>
          {/* Contract Overview */}
          <TabPanel value={tab} index={0}>
            {contractReport && (
              <Grid container spacing={3}>
                {/* By Status */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Contracts by Status</Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={contractReport.byStatus} margin={{ left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="status" tick={{ fontSize: 12 }} tickFormatter={(v: string) => v.charAt(0).toUpperCase() + v.slice(1)} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1565c0" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>

                {/* By Type */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Contracts by Type</Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={contractReport.summary} dataKey="count" nameKey="contract_type" cx="50%" cy="50%" outerRadius={80}
                        label={({ contract_type, count }) => `${contract_type} (${count})`}>
                        {contractReport.summary.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Grid>

                {/* Expiration Timeline */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Expirations - Next 12 Months</Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={contractReport.expirationTimeline} margin={{ left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v, name) => name === 'value' ? formatCurrency(v as number) : v} />
                      <Bar dataKey="count" name="Contracts" fill="#e53935" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>

                {/* Top Counterparties */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Top Counterparties</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Counterparty</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Contracts</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Total Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contractReport.topCounterparties.map((cp: any) => (
                          <TableRow key={cp.counterparty_name}>
                            <TableCell>{cp.counterparty_name}</TableCell>
                            <TableCell><Chip label={cp.counterparty_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                            <TableCell>{cp.contract_count}</TableCell>
                            <TableCell>{cp.total_value > 0 ? formatCurrency(cp.total_value) : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Renewals */}
          <TabPanel value={tab} index={1}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Typography variant="body1">Show contracts expiring within</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={renewalDays} onChange={(e) => setRenewalDays(e.target.value as number)}>
                  {[30, 60, 90, 180, 365].map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
                </Select>
              </FormControl>
              <Button onClick={loadReports} variant="outlined" size="small">Apply</Button>
            </Box>

            {renewalReport && (
              <>
                <Alert severity={renewalReport.renewals.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                  {renewalReport.renewals.length} contracts expiring in the next {renewalDays} days
                </Alert>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Counterparty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Owner</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Expiration Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Notice Deadline</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Days Left</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Auto-Renews</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renewalReport.renewals.map((c: any) => (
                        <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/contracts/${c.id}`)}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{c.contract_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.contract_type}</Typography>
                          </TableCell>
                          <TableCell>{c.counterparty_name}</TableCell>
                          <TableCell>{c.owner_name || '-'}</TableCell>
                          <TableCell>{formatDate(c.expiration_date)}</TableCell>
                          <TableCell>{c.renewal_notice_date ? formatDate(c.renewal_notice_date) : '-'}</TableCell>
                          <TableCell>
                            {c.days_until_expiration <= 0 ? (
                              <Chip label="Expired" color="error" size="small" />
                            ) : (
                              <Chip
                                label={`${c.days_until_expiration}d`}
                                color={c.days_until_expiration <= 30 ? 'error' : c.days_until_expiration <= 90 ? 'warning' : 'default'}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={c.auto_renews ? 'Yes' : 'No'} color={c.auto_renews ? 'success' : 'default'} size="small" />
                          </TableCell>
                          <TableCell>{c.contract_value ? formatCurrency(c.contract_value) : '-'}</TableCell>
                        </TableRow>
                      ))}
                      {renewalReport.renewals.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">No contracts expiring in this period</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </TabPanel>

          {/* Obligations & SLAs */}
          <TabPanel value={tab} index={2}>
            {obligationReport && (
              <Grid container spacing={3}>
                {/* Summary by Type */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Obligations by Type</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Overdue</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>At Risk</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {obligationReport.summary.map((row: any) => (
                          <TableRow key={row.obligation_type}>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{row.obligation_type}</TableCell>
                            <TableCell>{row.total}</TableCell>
                            <TableCell><Typography color="success.main">{row.completed}</Typography></TableCell>
                            <TableCell><Typography color="error.main">{row.overdue}</Typography></TableCell>
                            <TableCell><Typography color="warning.main">{row.at_risk}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Overdue */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Overdue Obligations</Typography>
                  {obligationReport.overdueObligations.length === 0 ? (
                    <Alert severity="success">No overdue obligations</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Obligation</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Days Overdue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {obligationReport.overdueObligations.slice(0, 10).map((ob: any) => (
                            <TableRow key={ob.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/obligations/${ob.id}`)}>
                              <TableCell>
                                <Typography variant="body2">{ob.obligation_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{ob.assigned_name || 'Unassigned'}</Typography>
                              </TableCell>
                              <TableCell>{ob.contract_name}</TableCell>
                              <TableCell>
                                <Chip label={`${ob.days_overdue}d overdue`} color="error" size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                {/* SLA Compliance */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>SLA Compliance</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>SLA</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Current</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Avg</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Non-Compliant</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {obligationReport.slaCompliance.map((sla: any) => (
                          <TableRow key={sla.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/obligations/${sla.id}`)}>
                            <TableCell>{sla.obligation_name}</TableCell>
                            <TableCell>{sla.contract_name}</TableCell>
                            <TableCell>{sla.sla_metric || '-'}</TableCell>
                            <TableCell>{sla.sla_target_value ?? '-'} {sla.sla_unit}</TableCell>
                            <TableCell>{sla.current_value ?? '-'}</TableCell>
                            <TableCell>{sla.avg_value != null ? Math.round(sla.avg_value * 100) / 100 : '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={sla.non_compliant_count || 0}
                                color={sla.non_compliant_count > 0 ? 'error' : 'success'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        {obligationReport.slaCompliance.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography color="text.secondary">No SLA obligations recorded</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}
